import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import styled from "styled-components";
import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import { ArrowBack, Business as BusinessIcon, Description, Search, NavigateNext, NavigateBefore } from "@material-ui/icons";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { v4 as uuidv4 } from "uuid";
import Empresas from './Empresas';
import {
    Container,
    ModernHeader,
    HeaderLeft,
    HeaderContent,
    PageTitle,
    PageSubtitle,
    HeaderActions,
    BackButton,
    NewButton,
    ResultsCard,
    SectionInfo,
    SectionInfoTitle,
    SectionSubtitle,
    TableWrapper,
    StreamsTable,
    TableHead,
    TableRow,
    TableCell,
    TableActions,
    ActionButton,
    EmptyState,
    EmptyTitle,
    EmptyText,
    getTheme,
} from "../../components/common";

export default function Business() {
    const history = useHistory();
    const theme = getTheme('concur');
    const [business, setBusiness] = useState([])
    const [isViewingBusiness, setIsViewingBusiness] = useState(false)
    const [viewingBusiness, setViewingBusiness] = useState(null)
    const [isLoading, setIsLoading] = useState(true)

    // Filter state
    const [searchTerm, setSearchTerm] = useState('')

    // Pagination state for records per page
    const [recordsPerPage, setRecordsPerPage] = useState(5)
    const [currentPage, setCurrentPage] = useState(1)

    // Filtered data
    const filteredBusiness = useMemo(() => {
        if (!searchTerm) return business
        const term = searchTerm.toLowerCase()
        return business.filter(item => {
            const cnpjMatch = item.cnpj && item.cnpj.toLowerCase().includes(term)
            const nameMatch = item.name && item.name.toLowerCase().includes(term)
            const descMatch = item.description && item.description.toLowerCase().includes(term)
            return cnpjMatch || nameMatch || descMatch
        })
    }, [business, searchTerm])

    // Paginated data
    const paginatedBusiness = useMemo(() => {
        const startIndex = (currentPage - 1) * recordsPerPage
        const endIndex = startIndex + recordsPerPage
        return filteredBusiness.slice(startIndex, endIndex)
    }, [filteredBusiness, currentPage, recordsPerPage])

    const totalPages = Math.ceil(filteredBusiness.length / recordsPerPage)

    // Reset to first page when filters change
    useEffect(() => {
        setCurrentPage(1)
    }, [searchTerm, recordsPerPage])

    const getBusiness = async () => {
        try {
            setIsLoading(true)
            let allData = [];
            let nextToken = null;

            do {
                const { data } = await fetchClient.get(`getAllAplicacao?limit=100${nextToken ? `&nextToken=${nextToken}` : ''}`);
                allData = [...allData, ...data.value];
                nextToken = data.nextToken;
            } while (nextToken);

            setBusiness(allData);
        } catch (err) {
            console.log(err);
            alert('Erro ao carregar aplicações');
        } finally {
            setIsLoading(false);
        }
    }

    const viewBusiness = async ({ newBusiness = false, ativo = null, cnpj = null, database = null, description = null, id, login = null, name = null, password = null, url = null, connected = null, metadata = false }) => {
        setIsViewingBusiness(true);
        setViewingBusiness({ ativo, cnpj, database, connected, description, id, login, name, password, url, new: newBusiness, metadata });
    }

    useEffect(() => {
        getBusiness();
        //eslint-disable-next-line
    }, [isViewingBusiness])

    return (
        <Container>
            {isViewingBusiness ? (
                <Empresas
                    data={viewingBusiness}
                    isViewing={setIsViewingBusiness}
                />
            ) : (
                <>
                    <ModernHeader>
                        <HeaderLeft>
                            <BackButton color={theme.primary} onClick={() => history.goBack()}>
                                <ArrowBack />
                            </BackButton>
                            <HeaderContent>
                                <PageTitle>
                                    <BusinessIcon style={{ marginRight: '12px', fontSize: '32px' }} />
                                    Empresas Concur
                                </PageTitle>
                                <PageSubtitle>Gerencie as empresas cadastradas no sistema</PageSubtitle>
                            </HeaderContent>
                        </HeaderLeft>
                        <HeaderActions>
                            <NewButton
                                gradient={theme.gradient}
                                onClick={() => viewBusiness({ id: uuidv4(), newBusiness: true })}
                            >
                                <PersonAddIcon style={{ marginRight: '8px' }} />
                                Nova Empresa
                            </NewButton>
                        </HeaderActions>
                    </ModernHeader>

                    {isLoading ? (
                        <LoaderWrapper>
                            <Loader />
                        </LoaderWrapper>
                    ) : business.length === 0 ? (
                        <ResultsCard>
                            <EmptyState>
                                <Description style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '16px' }} />
                                <EmptyTitle>Nenhuma empresa encontrada</EmptyTitle>
                                <EmptyText>Não há empresas cadastradas no sistema. Adicione uma nova empresa para começar.</EmptyText>
                            </EmptyState>
                        </ResultsCard>
                    ) : (
                        <ResultsCard>
                            <SectionInfoWrapper>
                                <SectionInfoLeft>
                                    <SectionInfoTitle>Empresas cadastradas</SectionInfoTitle>
                                    <SectionSubtitle>
                                        Mostrando {paginatedBusiness.length} de {filteredBusiness.length} {filteredBusiness.length === 1 ? 'empresa' : 'empresas'}
                                        {searchTerm && ` (filtrado de ${business.length})`}
                                    </SectionSubtitle>
                                </SectionInfoLeft>
                                <SearchContainer>
                                    <SearchIcon>
                                        <Search style={{ fontSize: '20px' }} />
                                    </SearchIcon>
                                    <SearchInput
                                        type="text"
                                        placeholder="Pesquisar por CNPJ, nome ou descrição..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <ClearSearchButton onClick={() => setSearchTerm('')}>
                                            ×
                                        </ClearSearchButton>
                                    )}
                                </SearchContainer>
                            </SectionInfoWrapper>

                            <TableWrapper>
                                <StreamsTable>
                                    <thead>
                                        <tr>
                                            <TableHead>CNPJ</TableHead>
                                            <TableHead>Nome</TableHead>
                                            <TableHead>Descrição</TableHead>
                                            <TableHead align="right">Ações</TableHead>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedBusiness.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} style={{ textAlign: 'center', color: '#6b7280' }}>
                                                    Nenhum resultado encontrado para os filtros aplicados
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            paginatedBusiness.map((item) => (
                                                <TableRow key={item.id}>
                                                    <TableCell>{item.cnpj}</TableCell>
                                                    <TableCell>{item.name}</TableCell>
                                                    <TableCell>{item.description}</TableCell>
                                                    <TableActions>
                                                        <ActionButton
                                                            gradient={theme.gradient}
                                                            onClick={() => viewBusiness(item)}
                                                        >
                                                            <Search style={{ marginRight: '6px', fontSize: '18px' }} />
                                                            Visualizar
                                                        </ActionButton>
                                                    </TableActions>
                                                </TableRow>
                                            ))
                                        )}
                                    </tbody>
                                </StreamsTable>
                            </TableWrapper>

                            <PaginationWrapper>
                                <RecordsPerPageContainer>
                                    <RecordsPerPageLabel>Registros por página:</RecordsPerPageLabel>
                                    <RecordsPerPageSelect
                                        value={recordsPerPage}
                                        onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                    >
                                        <option value={5}>5</option>
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </RecordsPerPageSelect>
                                </RecordsPerPageContainer>

                                <PaginationContainer>
                                    <PaginationButton
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    >
                                        <NavigateBefore />
                                    </PaginationButton>
                                    <PageIndicator>
                                        Página {currentPage} de {totalPages || 1}
                                    </PageIndicator>
                                    <PaginationButton
                                        disabled={currentPage >= totalPages}
                                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    >
                                        <NavigateNext />
                                    </PaginationButton>
                                </PaginationContainer>
                            </PaginationWrapper>
                        </ResultsCard>
                    )}
                </>
            )}
        </Container>
    );
}

// ===== COMPONENTES ESPECÍFICOS DESTA PÁGINA =====

const LoaderWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 320px;
    background: white;
    border-radius: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    width: 100%;
    box-sizing: border-box;
`;

const PaginationContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    margin-top: 24px;
`;

const PaginationButton = styled.button`
    width: 40px;
    height: 40px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%);
    color: #fff;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(142, 68, 173, 0.3);

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(142, 68, 173, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        background: #bdbdbd;
        cursor: not-allowed;
        box-shadow: none;
    }
`;

const PageIndicator = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
    padding: 0 16px;
`;

// ===== FILTER COMPONENTS =====

const SectionInfoWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding: 24px 16px 0 16px;
    gap: 16px;
`;

const SectionInfoLeft = styled.div`
    display: flex;
    flex-direction: column;
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    min-width: 300px;
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 12px;
    color: #9ca3af;
    display: flex;
    align-items: center;
    pointer-events: none;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 10px 40px 10px 44px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #8e44ad;
        box-shadow: 0 0 0 3px rgba(142, 68, 173, 0.1);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 8px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: #e5e7eb;
    color: #6b7280;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        background: #d1d5db;
        color: #374151;
    }
`;

// ===== PAGINATION COMPONENTS =====

const PaginationWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
`;

const RecordsPerPageContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RecordsPerPageLabel = styled.span`
    font-size: 14px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const RecordsPerPageSelect = styled.select`
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;

    &:focus {
        outline: none;
        border-color: #8e44ad;
    }
`;

const LoadMoreContainer = styled.div`
    display: flex;
    align-items: center;
`;

const LoadMoreButton = styled.button`
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background: ${props => props.gradient || 'linear-gradient(135deg, #8e44ad 0%, #9b59b6 100%)'};
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Mulish', sans-serif;
    box-shadow: 0 2px 8px rgba(142, 68, 173, 0.3);

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(142, 68, 173, 0.4);
    }

    &:active {
        transform: translateY(0);
    }
`;
